import { API_BASE_URL, ApiError, fetchWithAuth } from '../config/api';
import type { BudgetActualReport, PeriodFilter } from '../types/reporting.types';

export class ReportsApiError extends ApiError {
  constructor(message: string, status?: number, details?: unknown) {
    super(message, status, details);
    this.name = 'ReportsApiError';
  }
}

export const reportsApi = {
  /**
   * Fetch budget vs actual report for a specific period
   */
  async getBudgetActualReport(filter: PeriodFilter): Promise<BudgetActualReport> {
    const queryParams = new URLSearchParams({
      year: filter.year.toString(),
      period: filter.period_type,
    });

    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/reports/budget-actual?${queryParams}`
    );

    const data = await response.json();
    return data as BudgetActualReport;
  },

  /**
   * Get all tags for a specific costpoint within a date range
   */
  async getCostpointTags(
    costpoint: string,
    startDate: string,
    endDate: string
  ): Promise<unknown[]> {
    const queryParams = new URLSearchParams({
      costpoint,
      start_date: startDate,
      end_date: endDate,
    });

    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/reports/costpoint-tags?${queryParams}`
    );

    const data = await response.json();
    return data;
  },

  /**
   * Get all transactions for a specific costpoint with optional tag filtering
   */
  async getAllCostpointTransactions(
    costpoint: string,
    startDate: string,
    endDate: string,
    tag?: string
  ): Promise<unknown[]> {
    const queryParams = new URLSearchParams({
      costpoint,
      start_date: startDate,
      end_date: endDate,
    });

    if (tag) {
      queryParams.append('tag', tag);
    }

    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/reports/costpoint-all-transactions?${queryParams}`
    );

    const data = await response.json();
    return data;
  },

  /**
   * Health check for the reports API
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/health`);
    return await response.json();
  },
};
